import { formatAppDateTime } from "./appTimeFormat";

export const BETTOR_SORTS = [
  { value: "recent", label: "Recent bet" },
  { value: "stake_desc", label: "Top stake" },
  { value: "tickets_desc", label: "Most tickets" },
];

export const MIN_TICKET_PRESETS = [
  { value: 0, label: "Any tickets" },
  { value: 2, label: "2+ tickets" },
  { value: 5, label: "5+ tickets" },
  { value: 10, label: "10+ tickets" },
];

export const TICKET_STATUS = {
  pending: { label: "Open", tone: "muted", color: "#90A4AE" },
  won: { label: "Won", tone: "win", color: "#2ECC71" },
  paid: { label: "Paid", tone: "win", color: "#2ECC71" },
  lost: { label: "Lost", tone: "lost", color: "#EF5350" },
  voided: { label: "Voided", tone: "muted", color: "#90A4AE" },
  refunded: { label: "Refunded", tone: "muted", color: "#90A4AE" },
};

export const ticketStatusMeta = (status = "") =>
  TICKET_STATUS[String(status).toLowerCase()] || {
    label: status || "Unknown",
    tone: "muted",
    color: "#90A4AE",
  };

export const formatPlacedAt = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return formatAppDateTime(d);
};

export const formatOdds = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return `${n.toFixed(2)}×`;
};

export const ticketTapeSummary = (tickets = []) => {
  let open = 0;
  let stake = 0;
  let exposure = 0;

  for (const t of tickets) {
    const st = String(t.status || "").toLowerCase();
    stake += Number(t.stake) || 0;
    if (st === "pending" || Number(t.openLegs) > 0) {
      open += 1;
      exposure += Number(t.potentialPayout) || 0;
    }
  }

  return { count: tickets.length, open, stake, exposure };
};

export const bettorDateKey = (row) => {
  if (!row?.lastBetAt) return "unknown";
  const d = new Date(row.lastBetAt);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const formatRelative = (value) => {
  if (!value) return "—";
  const s = (Date.now() - new Date(value).getTime()) / 1000;
  if (s < 45) return "Just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 172800) return "Yesterday";
  return `${Math.floor(s / 86400)}d ago`;
};

export const shortTicketId = (id) => {
  const s = String(id || "");
  return s.length <= 8 ? s : `…${s.slice(-6)}`;
};

const FIXTURE_STATUS_LABELS = {
  NS: "Not started",
  TBD: "TBD",
  "1H": "Live · 1st half",
  HT: "Half time",
  "2H": "Live · 2nd half",
  ET: "Extra time",
  BT: "Break",
  P: "Penalties",
  LIVE: "Live",
  FT: "Full time",
  AET: "After extra time",
  PEN: "After penalties",
  PST: "Postponed",
  CANC: "Cancelled",
  ABD: "Abandoned",
  SUSP: "Suspended",
  INT: "Interrupted",
  WO: "Walkover",
};

const LIVE_FIXTURE_CODES = new Set(["1H", "2H", "HT", "ET", "BT", "P", "LIVE"]);

export const fixtureStatusMeta = (shortCode) => {
  const code = String(shortCode || "").toUpperCase();
  if (!code) return { code: null, label: "Scheduled", tone: "muted" };
  const label = FIXTURE_STATUS_LABELS[code] || code;
  if (LIVE_FIXTURE_CODES.has(code)) return { code, label, tone: "live" };
  if (code === "NS" || code === "TBD") return { code, label, tone: "muted" };
  if (["PST", "CANC", "ABD", "SUSP", "INT", "WO"].includes(code)) {
    return { code, label, tone: "warn" };
  }
  return { code, label, tone: "done" };
};

const SELECTION_PART_LABELS = {
  oneXTwo: "1X2",
  firstHalf: "1st half",
  secondHalf: "2nd half",
  fullTime: "Full time",
  overUnder: "Over / Under",
  bothTeamsToScore: "BTTS",
  doubleChance: "Double chance",
};

export const formatSelectionLabel = (raw = "") => {
  if (!raw || typeof raw !== "string") return "";
  return raw
    .split(" · ")
    .map((part) => SELECTION_PART_LABELS[part] || part)
    .join(" · ");
};

export const legHasScore = (leg) =>
  leg?.homeGoals != null &&
  leg?.awayGoals != null &&
  !Number.isNaN(Number(leg.homeGoals)) &&
  !Number.isNaN(Number(leg.awayGoals));

const LIVE_OR_DONE_FIXTURE_CODES = new Set([
  "1H",
  "2H",
  "HT",
  "ET",
  "BT",
  "P",
  "LIVE",
  "FT",
  "AET",
  "PEN",
  "AWD",
  "WO",
]);

const legFixtureCode = (leg) => {
  if (leg?.fixtureStatus) return String(leg.fixtureStatus).toUpperCase();
  const nested = leg?.fixture?.status?.short;
  if (nested) return String(nested).toUpperCase();
  const legStatus = String(leg?.status || "").toLowerCase();
  if (["won", "lost", "voided"].includes(legStatus)) return "FT";
  if (legStatus === "pending") return "NS";
  return "";
};

const parseScoreLabel = (label) => {
  if (!label || typeof label !== "string") return null;
  const match = label.match(/(\d+)\s*[–-]\s*(\d+)/);
  if (!match) return null;
  return {
    home: Number(match[1]),
    away: Number(match[2]),
    phase: null,
  };
};

const scoresFromFixturePayload = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  const home =
    payload.score?.fulltime?.home ??
    payload.goals?.home ??
    null;
  const away =
    payload.score?.fulltime?.away ??
    payload.goals?.away ??
    null;
  if (home == null && away == null) return null;
  return {
    home: Number(home ?? 0),
    away: Number(away ?? 0),
    phase: null,
  };
};

export const resolveLegScore = (leg) => {
  if (legHasScore(leg)) {
    return {
      home: Number(leg.homeGoals),
      away: Number(leg.awayGoals),
      phase: null,
    };
  }

  const fromLabel = parseScoreLabel(leg?.scoreLabel);
  if (fromLabel) return fromLabel;

  const fromFixture = scoresFromFixturePayload(leg?.fixture);
  if (fromFixture) return fromFixture;

  const fromNestedGoals = scoresFromFixturePayload({
    goals: leg?.goals,
    score: leg?.score,
  });
  if (fromNestedGoals) return fromNestedGoals;

  const code = legFixtureCode(leg);

  if (
    code === "HT" &&
    (leg?.halftimeHome != null || leg?.halftimeAway != null)
  ) {
    return {
      home: Number(leg.halftimeHome ?? 0),
      away: Number(leg.halftimeAway ?? 0),
      phase: "HT",
    };
  }

  const htHome = leg?.fixture?.score?.halftime?.home ?? leg?.score?.halftime?.home;
  const htAway = leg?.fixture?.score?.halftime?.away ?? leg?.score?.halftime?.away;
  if (code === "HT" && (htHome != null || htAway != null)) {
    return {
      home: Number(htHome ?? 0),
      away: Number(htAway ?? 0),
      phase: "HT",
    };
  }

  return null;
};

export const legMissingScoreResult = (leg, ticketStatus) => {
  if (resolveLegScore(leg)) return false;
  if (!leg?.fixtureId) return false;
  const code = legFixtureCode(leg);
  const legStatus = String(leg?.status || "").toLowerCase();
  const ticketSettled = ["won", "lost", "paid", "voided"].includes(
    String(ticketStatus || "").toLowerCase()
  );
  const expectsResult =
    LIVE_OR_DONE_FIXTURE_CODES.has(code) ||
    ["won", "lost"].includes(legStatus) ||
    ticketSettled;
  return (
    expectsResult &&
    !!(leg?.home?.name || leg?.away?.name || leg?.matchLabel)
  );
};

export const resolveLegFixtureStatus = (leg) => {
  const code = legFixtureCode(leg);
  if (code) return fixtureStatusMeta(code);
  return fixtureStatusMeta(null);
};

export const formatBettorCount = (n) => {
  const count = Number(n) || 0;
  return `${count} bettor${count === 1 ? "" : "s"}`;
};

export const normalizeBettorTickets = (tickets = []) =>
  (Array.isArray(tickets) ? tickets : []).map((ticket) => ({
    ...ticket,
    legs: Array.isArray(ticket.legs) ? ticket.legs : [],
  }));

export const ticketsMissingLegs = (tickets = []) =>
  (Array.isArray(tickets) ? tickets : []).some(
    (ticket) =>
      Number(ticket.legCount) > 0 &&
      (!Array.isArray(ticket.legs) || ticket.legs.length === 0)
  );

export const ticketMissingTeamLogos = (ticket) =>
  (Array.isArray(ticket?.legs) ? ticket.legs : []).some((leg) => {
    const homeMissing =
      !!leg?.home?.name &&
      !(typeof leg?.home?.logo === "string" && leg.home.logo.trim());
    const awayMissing =
      !!leg?.away?.name &&
      !(typeof leg?.away?.logo === "string" && leg.away.logo.trim());
    return homeMissing || awayMissing;
  });

export const ticketNeedsFixtureHydration = (ticket) =>
  (Number(ticket?.legCount) > 0 &&
    (!Array.isArray(ticket?.legs) || ticket.legs.length === 0)) ||
  ticketMissingTeamLogos(ticket) ||
  (Array.isArray(ticket?.legs) &&
    ticket.legs.some((leg) => legMissingScoreResult(leg, ticket?.status)));

export const mergeLegFixtureMeta = (leg, meta) => {
  if (!meta || typeof meta !== "object") return leg;
  const homeGoals =
    meta.goals?.home ?? meta.score?.fulltime?.home ?? leg.homeGoals ?? null;
  const awayGoals =
    meta.goals?.away ?? meta.score?.fulltime?.away ?? leg.awayGoals ?? null;
  const fixtureStatus =
    leg.fixtureStatus || meta.status?.short || leg.fixtureStatus || null;
  const scoreLabel =
    homeGoals != null && awayGoals != null
      ? `${homeGoals}–${awayGoals}`
      : leg.scoreLabel || null;

  return {
    ...leg,
    fixtureStatus,
    homeGoals: homeGoals != null ? Number(homeGoals) : leg.homeGoals ?? null,
    awayGoals: awayGoals != null ? Number(awayGoals) : leg.awayGoals ?? null,
    scoreLabel,
    fixture: {
      goals: meta.goals || leg.fixture?.goals || null,
      score: meta.score || leg.fixture?.score || null,
      status: meta.status || leg.fixture?.status || null,
    },
    halftimeHome:
      meta.score?.halftime?.home ?? leg.halftimeHome ?? null,
    halftimeAway:
      meta.score?.halftime?.away ?? leg.halftimeAway ?? null,
  };
};

export const ticketsNeedFixtureHydration = (tickets = []) =>
  (Array.isArray(tickets) ? tickets : []).some(ticketNeedsFixtureHydration);

/* ─── "Know your book" analytics — computed client-side from stored data ─── */

/** Compact number formatting matching the operator console (12.4K). */
export const compactNum = (value) => {
  const n = Number(value) || 0;
  const abs = Math.abs(n);
  const sign = n < 0 ? "−" : "";
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 10_000) return `${sign}${(abs / 1000).toFixed(0)}K`;
  if (abs >= 1000) return `${sign}${(abs / 1000).toFixed(1)}K`;
  return `${sign}${abs.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

export const WHALE_RISK_SHARE = 60;

/**
 * Share of total handle held by the top 1 / 3 / 5 bettors.
 * topBettors comes from dashboard.customers.topBettors; totalStaked is the
 * house-wide handle those shares are measured against.
 */
export const whaleConcentration = (topBettors = [], totalStaked = 0, totalBettors = 0) => {
  const rows = (Array.isArray(topBettors) ? topBettors : [])
    .map((b) => ({
      userId: b.userId,
      username: b.username,
      totalStaked: Number(b.totalStaked) || 0,
      ticketCount: Number(b.ticketCount) || 0,
    }))
    .filter((b) => b.totalStaked > 0)
    .sort((a, b) => b.totalStaked - a.totalStaked);
  const handle = Number(totalStaked) || 0;
  if (!rows.length || handle <= 0) return { loaded: false };

  const sumTop = (n) => rows.slice(0, n).reduce((s, r) => s + r.totalStaked, 0);
  const tiers = [1, 3, 5].map((n) => {
    const stake = Math.min(sumTop(n), handle);
    return {
      key: `top${n}`,
      label: n === 1 ? "Top bettor" : `Top ${n}`,
      count: Math.min(n, rows.length),
      stake,
      share: Math.min(100, Math.round((stake / handle) * 1000) / 10),
    };
  });
  const top5Share = tiers[2].share;

  return {
    loaded: true,
    tiers,
    top5Share,
    // Concentration only counts as a risk when the book is bigger than the top tier.
    risky: top5Share > WHALE_RISK_SHARE && Number(totalBettors) > 5,
    leader: rows[0],
    handle,
  };
};

/** Round a raw bucket width up to a friendly 1/2/5×10^k step. */
const niceStep = (raw) => {
  if (!Number.isFinite(raw) || raw <= 0) return 1;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const unit = raw / mag;
  if (unit <= 1) return mag;
  if (unit <= 2) return 2 * mag;
  if (unit <= 5) return 5 * mag;
  return 10 * mag;
};

/** Histogram of loaded bettor rows bucketed by totalStaked. */
export const stakeHistogram = (rows = [], bucketTarget = 6) => {
  const stakes = (Array.isArray(rows) ? rows : [])
    .map((r) => Number(r?.totalStaked) || 0)
    .filter((v) => v > 0);
  if (!stakes.length) return { loaded: false };

  const max = Math.max(...stakes);
  const step = niceStep(max / bucketTarget);
  const bucketCount = Math.max(2, Math.min(8, Math.ceil(max / step) || 1));
  const buckets = Array.from({ length: bucketCount }, (_, i) => ({
    key: `b${i}`,
    min: i * step,
    max: (i + 1) * step,
    label: `${compactNum(i * step)}–${compactNum((i + 1) * step)}`,
    count: 0,
  }));
  for (const v of stakes) {
    const idx = Math.min(bucketCount - 1, Math.floor(v / step));
    buckets[idx].count += 1;
  }

  return {
    loaded: true,
    buckets,
    maxCount: Math.max(...buckets.map((b) => b.count), 1),
    sample: stakes.length,
  };
};

/** Active-this-week / this-month / dormant split from lastBetAt of loaded rows. */
export const recencySplit = (rows = []) => {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return { loaded: false };

  const now = Date.now();
  let week = 0;
  let month = 0;
  let dormant = 0;
  for (const r of list) {
    const t = r?.lastBetAt ? new Date(r.lastBetAt).getTime() : NaN;
    if (!Number.isFinite(t)) {
      dormant += 1;
      continue;
    }
    const days = (now - t) / 86_400_000;
    if (days <= 7) week += 1;
    else if (days <= 30) month += 1;
    else dormant += 1;
  }

  return {
    loaded: true,
    total: list.length,
    segments: [
      { key: "week", label: "Active this week", count: week, tone: "up" },
      { key: "month", label: "Active this month", count: month, tone: "accent" },
      { key: "dormant", label: "Dormant 30d+", count: dormant, tone: "down" },
    ],
  };
};

/** Pulse from GET /api/bet/:houseId/customers — one-time vs repeat bettors from Betting records. */
export const normalizeBettorPulse = (pulse) => {
  if (!pulse || typeof pulse !== "object") {
    return { loaded: false };
  }

  return {
    loaded: true,
    totalBettors: Number(pulse.totalBettors) || 0,
    oneTimeBettors: Number(pulse.oneTimeBettors) || 0,
    repeatBettors: Number(pulse.repeatBettors) || 0,
    totalTickets: Number(pulse.totalTickets) || 0,
    totalStaked: Number(pulse.totalStaked) || 0,
  };
};
